import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, Spinner, Alert, Form, Row, Col, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaPaperPlane, FaGift, FaEnvelopeOpenText, FaWhatsapp, FaCheckCircle, FaTimesCircle, FaEye, FaSpinner, FaBell, FaSync } from 'react-icons/fa';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import './Payment-History.css';
import paymentApi from '../../api/payments/paymentApi';
import { useAuthContext } from '../../context/AuthContext';

const Payments = () => {
  const { user, events, isAuthenticated, currentEvent } = useAuthContext();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [statusFilter, setStatusFilter] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [qrPayment, setQrPayment] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const fetchPayments = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const params = { page: currentPage, per_page: perPage };
      if (statusFilter !== 'All') {
        params.status = statusFilter;
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const response = await paymentApi.getEventPayments(params);

      let payload = response?.data ?? response ?? {};
      let page = payload?.data ?? payload;
      let items = Array.isArray(page) ? page : page?.data ?? [];

      const formattedPayments = items.map(payment => ({
        id: payment.id,
        date: payment.created_at ? new Date(payment.created_at).toLocaleString() : 'N/A',
        transactionToken: payment.transtoken || payment.id,
        reference: payment.reference_number || `N/A`,
        for: getPaymentType(payment.paid_for),
        amount: payment.paid_amount || 0,
        paymentMethod: payment.payment_method || 'Unknown',
        status: payment.status === 1 ? 'Paid' : payment.status === 0 ? 'Pending' : 'Failed',
        phone: payment.phone_number,
        event_id: payment.event_id
      }));

      setPayments(formattedPayments);
      setTotalRecords(page?.total_records ?? page?.totalRecords ?? payload?.total_records ?? payload?.totalRecords ?? 0);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setPayments([]);
      setTotalRecords(0);
      if (err.response?.status !== 404) {
        setError('Failed to load payment history.');
        toast.error('Failed to load payment history');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, currentPage, perPage, statusFilter, startDate, endDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, startDate, endDate, perPage]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPayments();
    }
  }, [isAuthenticated, fetchPayments]);

  const getPaymentType = (paidFor) => {
    if (!paidFor) return 'Unknown';
    switch (paidFor) {
      case 'sms_credits': return 'SMS Credits Top-up';
      case 'whatsapp_credits': return 'WhatsApp Credits Top-up';
      case 'ecard': return 'E-Card Purchase';
      case 'ecard_resend':
      case 'ecards_resend': return 'E-Card Resend';
      default: return paidFor.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const handleViewDetails = async (payment) => {
    setSelectedPayment(payment);
    setShowDetailsModal(true);
    const paymentId = payment.id || payment.paymentId || payment.reference;
    if (!paymentId) return;

    setDetailsLoading(true);
    try {
      const response = await paymentApi.getPayment(paymentId);
      const payload = response?.data;
      const data = (payload && typeof payload === 'object' && payload.id) ? payload : (payload?.data || response || {});

      if (data && typeof data === 'object' && (data.id || data.reference_number)) {
        const formatted = {
          ...payment,
          id: data.id || payment.id,
          reference: data.reference_number || data.reference || data.payment_reference || data.order_id || payment.reference,
          date: data.created_at ? new Date(data.created_at).toLocaleString() : (data.date || payment.date),
          for: data.paid_for ? getPaymentType(data.paid_for) : (data.service || payment.for),
          amount: parseFloat(data.paid_amount ?? data.amount ?? data.total_amount ?? payment.amount ?? 0),
          credits: data.credits || null,
          customerName: data.customer_fname || 'N/A',
          paymentMethod: data.payment_method || data.channel || data.provider || payment.paymentMethod || 'Unknown',
          phone: data.phone_number || data.mobile || data.phone || payment.phone || 'N/A',
          status: Number(data.status) === 1 ? 'Paid' : Number(data.status) === 0 ? 'Pending' : 'Failed',
          transactionToken: data.transtoken || data.transactionToken || data.trans_id || data.trx_id || payment.transactionToken || 'N/A',
          rawDetails: data
        };
        setSelectedPayment(formatted);
      }
    } catch (err) {
      console.error('Error fetching single payment details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleShowQr = (payment) => {
    setQrPayment(payment);
    setShowQrModal(true);
  };

  const handleRetryPayment = async () => {
    if (!qrPayment?.transactionToken) return;
    setIsRetrying(true);
    try {
      const response = await paymentApi.retryPayment(qrPayment.transactionToken);
      handleApiResponse(response);
    } catch (err) {
      handleApiResponse(err);
    } finally {
      setIsRetrying(false);
    }
  };

  const columns = [
    {
      name: 'Date',
      selector: (row) => row.date,
      sortable: true,
      width: '200px'
    },
    {
      name: 'Reference',
      selector: (row) => row.reference,
      sortable: true
    },
    {
      name: 'Payment For',
      selector: (row) => row.for,
      sortable: true
    },
    {
      name: 'Amount (TZS)',
      selector: (row) => row.amount.toLocaleString(),
      sortable: true,
      width: '130px'
    },
    {
      name: 'Payment Method',
      selector: (row) => row.paymentMethod,
      sortable: true,
      width: '150px'
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      width: '120px',
      cell: (row) => (
        <span className={`payment-status status-${row.status.toLowerCase()}`}>
          {row.status === 'Paid' ? '✅ Paid' : row.status === 'Pending' ? '⏳ Pending' : '❌ Failed'}
        </span>
      )
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <Button
            variant="link"
            size="sm"
            className="view-details-btn"
            onClick={() => handleViewDetails(row)}
            title="View Details"
          >
            <FaEye /> View
          </Button>
          {row.status === 'Pending' && (
            <Button
              variant="link"
              size="sm"
              className="qr-action-btn"
              onClick={() => handleShowQr(row)}
              title="Resend Push Notification"
            >
              <FaBell /> Pay
            </Button>
          )}
        </div>
      ),
      width: '140px'
    }
  ];



  if (loading) {
    return (
      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <FaSpinner className="fa-spin" size={48} color="#4169e1" />
              <p className="mt-3" style={{ color: '#6b7280' }}>Loading payment history...</p>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="shadow-sm">
        <br></br>
        <Card.Body>
          <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Payments History</h1>

          <div className="dashboard row mt-4">
            <div className="dashboard col text-center">
              <Link to="/sms-topup" className="text-decoration-none">
                <div className="dashboard bg-dash p-4 rounded-lg">
                  <div className="dashText">
                    <FaPaperPlane className="mb-2" size={24} />
                    <div>Message TopUp</div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="dashboard col text-center">
              <Link to="/ecards" className="text-decoration-none">
                <div className="dashboard bg-dash p-4 rounded-lg">
                  <div className="dashText">
                    <FaEnvelopeOpenText className="mb-2" size={24} />
                    <div>Buy Ecards</div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="dashboard col text-center">
              <Link to="/sms-topup" className="text-decoration-none">
                <div className="dashboard bg-dash p-4 rounded-lg">
                  <div className="dashText">
                    <FaWhatsapp className="mb-2" size={24} />
                    <div>WhatsApp Credit</div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="dashboard col text-center">
              <Link to="#" onClick={(e) => { e.preventDefault(); setShowDiscountModal(true); }} className="text-decoration-none">
                <div className="dashboard bg-dash p-4 rounded-lg">
                  <div className="dashText">
                    <FaGift className="mb-2" size={24} />
                    <div>Discount</div>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          <div className="mt-4">

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="All">All Statuses</option>
                    <option value="1">Paid </option>
                    <option value="0">Pending </option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

            <StyledDataTable
              columns={columns}
              data={payments}
              paginationServer
              paginationTotalRows={totalRecords}
              paginationDefaultPage={currentPage}
              onChangePage={setCurrentPage}
              paginationPerPage={perPage}
              onChangeRowsPerPage={setPerPage}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              emptyMessage="No payment records found."
            />
          </div>
        </Card.Body>
      </Card>

      {/* Payment Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="md">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: '#1e293b', fontSize: '1.2rem' }}>
            Payment Details #{selectedPayment?.id || ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {detailsLoading ? (
            <div className="text-center py-4">
              <FaSpinner className="fa-spin text-primary mb-2" size={36} />
              <p className="text-muted small mb-0">Loading payment details...</p>
            </div>
          ) : selectedPayment ? (
            <Row className="g-3">
              <Col xs={6}>
                <div className="text-muted small">Reference</div>
                <div className="fw-bold text-truncate" style={{ fontSize: '0.95rem', color: '#334155' }}>
                  {selectedPayment.reference}
                </div>
              </Col>
              <Col xs={6}>
                <div className="text-muted small">Status</div>
                <div>
                  <span className={`payment-status status-${selectedPayment.status.toLowerCase()}`}>
                    {selectedPayment.status === 'Paid' ? '✅ Paid' : selectedPayment.status === 'Pending' ? '⏳ Pending' : '❌ Failed'}
                  </span>
                  {selectedPayment.resultExplanation && (
                    <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
                      ({selectedPayment.resultExplanation})
                    </small>
                  )}
                </div>
              </Col>
              <Col xs={12}>
                <div className="text-muted small">Payment For</div>
                <div className="fw-bold text-dark">{selectedPayment.for}</div>
              </Col>
              <Col xs={6}>
                <div className="text-muted small">Amount</div>
                <div className="fw-bold text-success" style={{ fontSize: '1.05rem' }}>
                  TZS {selectedPayment.amount ? selectedPayment.amount.toLocaleString() : '0'}
                </div>
              </Col>
              {selectedPayment.credits && (
                <Col xs={6}>
                  <div className="text-muted small">Credits Acquired</div>
                  <div className="fw-bold text-primary">
                    {parseFloat(selectedPayment.credits).toLocaleString()} Credits
                  </div>
                </Col>
              )}
              <Col xs={6}>
                <div className="text-muted small">Customer Name</div>
                <div className="fw-bold text-dark">{selectedPayment.customerName || 'N/A'}</div>
              </Col>
              <Col xs={6}>
                <div className="text-muted small">Phone Number</div>
                <div className="fw-bold text-dark">{selectedPayment.phone || 'N/A'}</div>
              </Col>
              <Col xs={6}>
                <div className="text-muted small">Payment Method</div>
                <div className="fw-bold text-dark">{selectedPayment.paymentMethod}</div>
              </Col>
              <Col xs={6}>
                <div className="text-muted small">Date Created</div>
                <div className="fw-bold text-secondary" style={{ fontSize: '0.85rem' }}>{selectedPayment.date}</div>
              </Col>
              <Col xs={12}>
                <div className="text-muted small">Transaction Token</div>
                <div className="fw-bold text-break bg-light p-2 rounded border" style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                  {selectedPayment.transactionToken}
                </div>
              </Col>
            </Row>
          ) : (
            <div className="text-center py-3 text-muted">No details available.</div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Body className="text-center py-4">
          <FaCheckCircle size={64} color="#28a745" />
          <h4 className="mt-3">Success!</h4>
          <p>{modalMessage}</p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="success" onClick={() => setShowSuccessModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Modal */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Body className="text-center py-4">
          <FaTimesCircle size={64} color="#dc3545" />
          <h4 className="mt-3">Error!</h4>
          <p>{modalMessage}</p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="danger" onClick={() => setShowErrorModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Discount Conditions Modal */}
      <Modal
        show={showDiscountModal}
        onHide={() => setShowDiscountModal(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="w-100 text-center fw-bold mt-3" style={{ color: '#4169e1', fontSize: '1.4rem', letterSpacing: '-0.3px' }}>
            Discount Conditions
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          <p className="text-muted text-center mb-4" style={{ fontSize: '0.9rem' }}>
            Mteja anapata discount kwenye kadi pindi anapokidhi vigezo vifuatavyo:
          </p>
          <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', color: '#334155', lineHeight: '1.7' }}>
            <li className="mb-3">
              Awe ametumia mfumo kwa muda mrefu kuanzia kutuma meseji za taarifa na michango.
            </li>
            <li className="mb-3">
              Awe amenunua meseji za matumizi kisichopungua kiasi cha shilingi laki moja (<strong>TZS 100,000</strong>).
            </li>
            <li className="mb-3">
              Discount ni kwenye kadi tu; kwenye SMS credits na WhatsApp credits haihusiki.
            </li>
          </ul>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <a
              href={`https://wa.me/255747636516?text=${encodeURIComponent(`Habari! Naomba Discount kwenye matumizi yangu ya kadi kupitia Harusi Online kwa shughuli ya: ${currentEvent?.event_title || currentEvent?.name || ''}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-success rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 fw-semibold"
              style={{ boxShadow: '0 4px 12px rgba(40, 167, 69, 0.2)' }}
            >
              <FaWhatsapp size={16} /> Request on WhatsApp
            </a>
            <Button
              onClick={() => setShowDiscountModal(false)}
              style={{
                backgroundColor: '#64748b',
                border: 'none',
                borderRadius: '25px',
                padding: '8px 30px',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(100, 116, 139, 0.2)'
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* QR Code Payment Modal */}
      {qrPayment && (
        <Modal show={showQrModal} onHide={() => setShowQrModal(false)} centered size="md">
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="w-100">
              <div className="d-flex align-items-center gap-2">
                <FaBell size={20} className="text-primary" />
                <div>
                  <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#1a237e' }}>Resend Push Notification</div>
                  <div className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 400 }}>Re-trigger mobile money push to complete payment</div>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="px-4 pb-4 pt-2">
            {/* Status pulse badge */}
            <div className="text-center mb-3">
              <Badge className="qr-pending-badge px-3 py-2">
                <span className="qr-pulse-dot" />
                Pending Payment
              </Badge>
            </div>

            {/* Payment summary */}
            <div className="qr-details-card mb-4">
              <Row className="g-2">
                <Col xs={6}>
                  <div className="qr-detail-item">
                    <span className="qr-detail-label">Amount</span>
                    <span className="qr-detail-value text-success fw-bold">TZS {qrPayment.amount?.toLocaleString()}</span>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="qr-detail-item">
                    <span className="qr-detail-label">For</span>
                    <span className="qr-detail-value">{qrPayment.for}</span>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="qr-detail-item">
                    <span className="qr-detail-label">Method</span>
                    <span className="qr-detail-value">{qrPayment.paymentMethod}</span>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="qr-detail-item">
                    <span className="qr-detail-label">Reference</span>
                    <span className="qr-detail-value text-break" style={{ fontSize: '0.8rem' }}>{qrPayment.reference}</span>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Resend push */}
            <div className="text-center">
              <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                <FaBell className="me-1" />
                Resend a push notification to <strong>{qrPayment.phone || 'your registered phone'}</strong> to complete payment.
              </p>
              <Button
                className="qr-retry-btn px-4 py-2"
                onClick={handleRetryPayment}
                disabled={isRetrying}
              >
                {isRetrying ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Sending…</>
                ) : (
                  <><FaSync className="me-2" />Resend Push Notification</>
                )}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default Payments;